import { Category, ProductDraft } from "@/types/general";
import {
    declOfNum,
    genT,
    PredefinedTranslations,
    toastError,
    useFilters,
    useOrdering,
    usePagination,
} from "@helpers/utils";
import BaseAdminFilter from "@reusables/BaseAdminFilter";
import BaseBadge from "@reusables/BaseBadge";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseMaterialCheckbox from "@reusables/BaseMaterialCheckbox";
import BasePermissionBlocker from "@reusables/BasePermissionBlocker";
import BaseTable from "@reusables/BaseTable";
import BaseTablePaginationControls from "@reusables/BaseTablePaginationControls";
import BaseTooltip from "@reusables/BaseTooltip";
import { BaseLoadingBlocker } from "@reusables/blockers/BaseLoadingBlocker";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import UIButton from "@reusables/UIButton";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";

import { Can, useAbility } from "@/casl.config";
import NoPermissionBanner from "@/components/ErrorPages/NoPermissionBanner";
import { useGetFileInfoMutation } from "@/redux/features/importApi";
import { useGetProductDraftsQuery } from "@/redux/features/productDrafts/productDraftsApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { ReactComponent as CheckSVG } from "@assets/icons/ic_check.svg";
import { ReactComponent as CrossSVG } from "@assets/icons/ic_cross.svg";
import { ReactComponent as EditSVG } from "@assets/icons/ic_edit.svg";
import { ReactComponent as TrashSVG } from "@assets/icons/ic_trash.svg";
import { ReactComponent as AllProductsIcon } from "@assets/icons/sidebar/ic_small_bag.svg";
import CategoryCommandDropdown from "@ready/CategoryCommandDropdown";
import { selectOperation } from "@redux/features/dfs/dfsSlice";
import { isDataTransferStateActive } from "@redux/features/dfs/types";
import { setImportData, setImportFile } from "@redux/features/importInfo/importInfoSlice";
import _ from "lodash";
import { Box, Forklift, PackageCheck } from "lucide-react";
import DeletionModal from "../components/modals/DeletionModal";
import MaterializationModal from "../components/modals/MaterializationModal";
import ProductDraftsExportModal from "../components/modals/ProductDraftsExportModal";
import ProductCode from "./parts/ProductCode";
import ProductDraftsImportModal from "./parts/ProductDraftsImportModal";

type StatusOption = {
    key: ProductDraft.Root["status"];
    label: string;
};

type BooleanOption = {
    id: 0 | 1;
    label: string;
};

export default function ProductDraftsPage() {
    const history = useHistory();
    const { t } = useTranslation("", { keyPrefix: "productDrafts.allProducts" });
    const ability = useAbility();
    const dispatch = useAppDispatch();

    const pagination = usePagination({ storageKey: "productDrafts.main" });
    const [selectedDraftIds, setSelectedDraftIds] = useState<number[]>([]);
    const tableInteractionRef = useRef<HTMLDivElement>(null);
    const isProductDraftExportActiveRef = useRef(false);

    const clearSelection = useCallback(() => setSelectedDraftIds([]), []);
    const resetForFilterChange = useCallback(() => {
        clearSelection();
        pagination.setPage(1);
    }, [clearSelection, pagination.setPage]);

    const [searchingValue, setSearchingValue] = useState<string>();
    const debouncedSearch = useMemo(() => _.debounce(setSearchingValue, 500), []);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearch = useCallback(
        (value: string) => {
            resetForFilterChange();
            debouncedSearch(value);
        },
        [debouncedSearch, resetForFilterChange]
    );

    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

    const componentsOptions = useMemo<BooleanOption[]>(
        () => [
            { id: 0, label: t("filters.dropdowns.components.options.0") },
            { id: 1, label: t("filters.dropdowns.components.options.1") },
        ],
        [t]
    );
    const [selectedComponent, setSelectedComponent] = useState<BooleanOption>();

    const typeOptions = useMemo<BooleanOption[]>(
        () => [
            { id: 0, label: t("filters.dropdowns.types.options.0") },
            { id: 1, label: t("filters.dropdowns.types.options.1") },
        ],
        [t]
    );
    const [selectedType, setSelectedType] = useState<BooleanOption>();

    const statusOptions = useMemo<StatusOption[]>(
        () => [
            { key: "draft", label: t("table.status.draft") },
            { key: "failed", label: t("table.status.failed") },
            { key: "exported", label: t("table.status.materialized") },
        ],
        [t]
    );
    const [selectedStatus, setSelectedStatus] = useState<StatusOption>();

    const { orderBy, setOrderBy } = useOrdering<ProductDraft.DTO.OrderBy>({
        name: "id",
        type: "desc",
    });

    const filters = useFilters<ProductDraft.DTO.Filters>(
        () => ({
            search: searchingValue,
            categories: selectedCategoryIds,
            components: selectedComponent?.id,
            type: selectedType?.id,
            status: selectedStatus?.key,
        }),
        [searchingValue, selectedCategoryIds, selectedComponent, selectedType, selectedStatus]
    );

    useEffect(() => {
        clearSelection();
    }, [clearSelection, filters]);

    const { data: productDraftsData, isFetching: productDraftsLoading } = useGetProductDraftsQuery(
        {
            filters,
            orderBy,
            pagination: pagination.request,
        },
        {
            skip: ability.cannot("view", "product"),
        }
    );

    const exportOperation = useAppSelector(selectOperation("export"));
    const isProductDraftExportActive =
        isDataTransferStateActive(exportOperation) && exportOperation.module === "product_draft";
    isProductDraftExportActiveRef.current = isProductDraftExportActive;

    useEffect(() => {
        const tableInteractionElement = tableInteractionRef.current;

        if (!tableInteractionElement) return;

        if (isProductDraftExportActive) {
            tableInteractionElement.setAttribute("inert", "");
            return;
        }

        tableInteractionElement.removeAttribute("inert");
    }, [isProductDraftExportActive]);

    const currentPageDrafts = productDraftsData?.payload ?? [];
    const eligibleCurrentPageIds = currentPageDrafts
        .filter((draft) => draft.status !== "exported")
        .map((draft) => draft.id);
    const allCurrentPageSelected =
        eligibleCurrentPageIds.length > 0 &&
        eligibleCurrentPageIds.every((id) => selectedDraftIds.includes(id));
    const someCurrentPageSelected = eligibleCurrentPageIds.some((id) =>
        selectedDraftIds.includes(id)
    );

    useEffect(() => {
        const newlyIneligibleIds = new Set(
            currentPageDrafts
                .filter((draft) => draft.status === "exported")
                .map((draft) => draft.id)
        );

        if (newlyIneligibleIds.size) {
            setSelectedDraftIds((previous) => previous.filter((id) => !newlyIneligibleIds.has(id)));
        }
    }, [currentPageDrafts]);

    const toggleCurrentPageSelection = (checked: boolean) => {
        setSelectedDraftIds((previous) => {
            if (checked) {
                return Array.from(new Set([...previous, ...eligibleCurrentPageIds]));
            }

            const currentPageIds = new Set(eligibleCurrentPageIds);
            return previous.filter((id) => !currentPageIds.has(id));
        });
    };

    const toggleDraftSelection = (draftId: number, checked: boolean) => {
        setSelectedDraftIds((previous) => {
            if (checked) {
                return previous.includes(draftId) ? previous : [...previous, draftId];
            }

            return previous.filter((id) => id !== draftId);
        });
    };

    const [getFileInfo] = useGetFileInfoMutation();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isMaterializationModalOpen, setIsMaterializationModalOpen] = useState(false);
    const [activeProductDraftDeletion, setActiveProductDraftDeletion] =
        useState<ProductDraft.Root>();

    useEffect(() => {
        if (!isProductDraftExportActive) return;

        setIsImportModalOpen(false);
        setIsExportModalOpen(false);
        setActiveProductDraftDeletion(undefined);
    }, [
        activeProductDraftDeletion,
        isExportModalOpen,
        isImportModalOpen,
        isProductDraftExportActive,
    ]);

    const renderStatusBadge = (draft: ProductDraft.Root) => {
        if (draft.status === "draft") {
            return <BaseBadge className="bg-badge-yellow">{t("table.status.draft")}</BaseBadge>;
        }

        if (draft.status === "failed") {
            const error = String(draft.export_error || t("table.status.errorUnavailable"));

            return (
                <BaseTooltip title={error} variant="error" placement="top">
                    <span
                        className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error focus-visible:ring-offset-2"
                        tabIndex={0}
                        aria-label={`${t("table.status.failed")}: ${error}`}
                    >
                        <BaseBadge className="bg-badge-red">
                            <span
                                aria-hidden="true"
                                className="mr-[8px] h-[7px] w-[7px] rounded-full bg-error animate-pulse motion-reduce:animate-none"
                            />
                            {t("table.status.failed")}
                        </BaseBadge>
                    </span>
                </BaseTooltip>
            );
        }

        if (draft.exported_product_id == null) {
            throw new Error(
                `Materialized Product Draft [${draft.id}] is missing exported_product_id`
            );
        }

        const badge = (
            <BaseBadge className="bg-badge-green">{t("table.status.materialized")}</BaseBadge>
        );

        return (
            <Link
                to={`/dashboard/products/${draft.exported_product_id}/details`}
                aria-label={t("table.actions.openMaterializedProduct")}
                className="inline-flex rounded-md transition-opacity duration-150 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-purple focus-visible:ring-offset-2 motion-reduce:transition-none"
            >
                {badge}
            </Link>
        );
    };

    const titleDeclinations = t("table.titleDeclinations", {
        returnObjects: true,
    }) as unknown as string[];
    const totalDrafts = productDraftsData?.meta.total ?? 0;

    return (
        <>
            <BaseAdminFilter
                permissionModule="product"
                config={{ importTitle: t("filters.import.title") }}
                filterItems={
                    <div className="w-[363px]">
                        <BaseInputsGrid cols={1} gap={24}>
                            <div>
                                <p className="mb-2 font-semibold text-inputs-label">
                                    {t("filters.dropdowns.category.label")}
                                </p>
                                <CategoryCommandDropdown
                                    multiple
                                    module="product"
                                    value={selectedCategoryIds}
                                    onSelect={(event) => {
                                        const ids = event.all
                                            .map((selection) => selection.item as Category.Root)
                                            .filter(Boolean)
                                            .map((category) => category.id);

                                        resetForFilterChange();
                                        setSelectedCategoryIds(ids);
                                    }}
                                />
                            </div>

                            <BaseDropdown
                                label={t("filters.dropdowns.components.label")}
                                options={componentsOptions}
                                value={selectedComponent}
                                getter={{
                                    label: (option) => option.label,
                                    key: (option) => option.id,
                                }}
                                onChange={(option) => {
                                    resetForFilterChange();
                                    setSelectedComponent(option);
                                }}
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                brightLabel
                            />

                            <BaseDropdown
                                label={t("filters.dropdowns.types.label")}
                                options={typeOptions}
                                value={selectedType}
                                getter={{
                                    label: (option) => option.label,
                                    key: (option) => option.id,
                                }}
                                onChange={(option) => {
                                    resetForFilterChange();
                                    setSelectedType(option);
                                }}
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                brightLabel
                            />

                            <BaseDropdown
                                label={t("filters.dropdowns.status.label")}
                                options={statusOptions}
                                value={selectedStatus}
                                getter={{
                                    label: (option) => option.label,
                                    key: (option) => option.key,
                                }}
                                onChange={(option) => {
                                    resetForFilterChange();
                                    setSelectedStatus(option);
                                }}
                                emptyValue={genT(PredefinedTranslations.DropdownsALL)}
                                brightLabel
                            />
                        </BaseInputsGrid>
                    </div>
                }
                handleSearch={handleSearch}
                handleFilter={() => undefined}
                handleImport={(file) => {
                    if (!file) return;

                    const formData = new FormData();
                    formData.append("file", file);

                    getFileInfo(formData)
                        .unwrap()
                        .then((data) => {
                            if (isProductDraftExportActiveRef.current) return;

                            dispatch(setImportData(data));
                            dispatch(setImportFile(file));
                            setIsImportModalOpen(true);
                        })
                        .catch(toastError);
                }}
                handleExport={() => setIsExportModalOpen(true)}
                isImportDisabled={isProductDraftExportActive}
                isExportDisabled={isProductDraftExportActive}
                disabledActionReason={t("actions.processing")}
                primaryAction={
                    <BaseTooltip
                        active={isProductDraftExportActive}
                        title={String(t("actions.processing"))}
                        placement="bottom"
                    >
                        <BasePermissionBlocker action="create" module="product">
                            <UIButton
                                type="button"
                                size="lg"
                                className="h-[56px]"
                                disabled={
                                    selectedDraftIds.length === 0 ||
                                    isProductDraftExportActive ||
                                    ability.cannot("create", "product")
                                }
                                onClick={() => setIsMaterializationModalOpen(true)}
                            >
                                <PackageCheck size={18} aria-hidden="true" />
                                <span>
                                    {t("actions.materialize", {
                                        count: selectedDraftIds.length,
                                    })}
                                </span>
                            </UIButton>
                        </BasePermissionBlocker>
                    </BaseTooltip>
                }
            />

            <div className="levitation-extended mt-[32px]">
                <Can not I="view" a="product">
                    <NoPermissionBanner />
                </Can>

                <Can I="view" a="product">
                    <div className="mb-[24px] flex items-center justify-between">
                        <div className="bold-highlight capitalize">
                            {totalDrafts} {declOfNum(totalDrafts, titleDeclinations)}
                        </div>

                        <UIButton
                            as={Link}
                            to="/dashboard/products"
                            variant="secondary"
                            size="md"
                            aria-label={t("returnToProducts")}
                        >
                            <AllProductsIcon width={16} height={16} aria-hidden="true" />
                            <span>{t("returnToProducts")}</span>
                        </UIButton>
                    </div>

                    <BaseLoadingBlocker active={isProductDraftExportActive}>
                        <div
                            ref={tableInteractionRef}
                            aria-busy={isProductDraftExportActive}
                            aria-disabled={isProductDraftExportActive}
                            className={
                                isProductDraftExportActive
                                    ? "pointer-events-none select-none"
                                    : undefined
                            }
                        >
                            <BaseTable
                                data={currentPageDrafts}
                                rowKeyGetter={(draft) => draft.id}
                                columns={[
                                    {
                                        header: (
                                            <BaseTooltip
                                                title={String(t("table.selection.currentPageHint"))}
                                                placement="top"
                                            >
                                                <span className="inline-flex">
                                                    <BaseMaterialCheckbox
                                                        checked={allCurrentPageSelected}
                                                        indeterminate={
                                                            someCurrentPageSelected &&
                                                            !allCurrentPageSelected
                                                        }
                                                        disabled={
                                                            eligibleCurrentPageIds.length === 0 ||
                                                            isProductDraftExportActive ||
                                                            ability.cannot("create", "product")
                                                        }
                                                        hitAreaPadding="6px"
                                                        ariaLabel={t("table.selection.currentPage")}
                                                        onChange={(_, checked) =>
                                                            toggleCurrentPageSelection(checked)
                                                        }
                                                    />
                                                </span>
                                            </BaseTooltip>
                                        ),
                                        getter: (draft, _, isCollapsed) =>
                                            draft.status === "exported" ? null : (
                                                <BaseMaterialCheckbox
                                                    checked={selectedDraftIds.includes(draft.id)}
                                                    disabled={
                                                        isProductDraftExportActive ||
                                                        ability.cannot("create", "product")
                                                    }
                                                    hitAreaPadding="6px"
                                                    borderColor={
                                                        isCollapsed ? "#3C3769" : undefined
                                                    }
                                                    ariaLabel={t("table.selection.row", {
                                                        name: draft.product.name,
                                                    })}
                                                    onChange={(_, checked) =>
                                                        toggleDraftSelection(draft.id, checked)
                                                    }
                                                />
                                            ),
                                        preventCollapsePropagation: true,
                                    },
                                    {
                                        header: t("table.columns.product"),
                                        getter: (draft) => {
                                            const content = (
                                                <>
                                                    <div className="bold-highlight">
                                                        {draft.product.name}
                                                    </div>
                                                    <ProductCode
                                                        code={draft.product.code}
                                                        supplierCode={
                                                            draft.product.supplier_product_code
                                                        }
                                                    />
                                                </>
                                            );

                                            if (
                                                draft.status === "exported" &&
                                                draft.exported_product_id
                                            ) {
                                                return (
                                                    <Link
                                                        className="inline-block rounded-md transition-opacity duration-150 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-purple focus-visible:ring-offset-2 motion-reduce:transition-none"
                                                        to={`/dashboard/products/${draft.exported_product_id}/details`}
                                                    >
                                                        {content}
                                                    </Link>
                                                );
                                            }

                                            if (
                                                draft.status !== "exported" &&
                                                ability.can("edit", "product")
                                            ) {
                                                return (
                                                    <Link
                                                        className="inline-block rounded-md transition-opacity duration-150 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-purple focus-visible:ring-offset-2 motion-reduce:transition-none"
                                                        to={`/dashboard/product-drafts/${draft.id}/edit`}
                                                    >
                                                        {content}
                                                    </Link>
                                                );
                                            }

                                            return <div>{content}</div>;
                                        },
                                        comparator: () => 0,
                                    },
                                    {
                                        header: t("table.columns.status"),
                                        getter: renderStatusBadge,
                                    },
                                    {
                                        header: t("table.columns.type"),
                                        getter: (draft) =>
                                            draft.product.is_service ? (
                                                <BaseBadge className="bg-light-green">
                                                    <Forklift
                                                        size={12}
                                                        className="mr-1"
                                                        aria-hidden="true"
                                                    />
                                                    <span>{t("table.isService")}</span>
                                                </BaseBadge>
                                            ) : (
                                                <BaseBadge className="bg-purple-400 text-white">
                                                    <Box
                                                        size={12}
                                                        className="mr-1 -mt-0.5"
                                                        aria-hidden="true"
                                                    />
                                                    <span>{t("table.isProduct")}</span>
                                                </BaseBadge>
                                            ),
                                    },
                                    {
                                        header: t("table.columns.category"),
                                        getter: (draft) => draft.product.category?.name ?? "-",
                                    },
                                    {
                                        header: t("table.columns.sellingPrice"),
                                        getter: (draft) => {
                                            const sellingPrice = draft.product.prices.selling_price;
                                            return typeof sellingPrice === "number"
                                                ? sellingPrice.toFixed(2)
                                                : "-";
                                        },
                                        comparator: () => 0,
                                    },
                                    {
                                        header: () => (
                                            <div className="text-center">
                                                {t("table.columns.component")}
                                            </div>
                                        ),
                                        getter: (draft, _, isCollapsed) => (
                                            <div className="text-center">
                                                {draft.product.is_component ? (
                                                    <CheckSVG
                                                        aria-hidden="true"
                                                        focusable="false"
                                                        className="text-tables-successIcon"
                                                    />
                                                ) : (
                                                    <CrossSVG
                                                        aria-hidden="true"
                                                        focusable="false"
                                                        className={
                                                            isCollapsed
                                                                ? "text-accent"
                                                                : "text-tables-secondaryIcon"
                                                        }
                                                    />
                                                )}
                                                <span className="sr-only">
                                                    {draft.product.is_component
                                                        ? t(
                                                              "filters.dropdowns.components.options.1"
                                                          )
                                                        : t(
                                                              "filters.dropdowns.components.options.0"
                                                          )}
                                                </span>
                                            </div>
                                        ),
                                    },
                                    {
                                        header: () => (
                                            <div className="text-center">
                                                {t("table.columns.actions")}
                                            </div>
                                        ),
                                        getter: (draft, _, isCollapsed) => {
                                            if (draft.status === "exported") return null;

                                            return (
                                                <div className="text-center space-x-[12px]">
                                                    <BasePermissionBlocker
                                                        action="edit"
                                                        module="product"
                                                    >
                                                        <EditSVG
                                                            className={`${
                                                                isCollapsed
                                                                    ? "text-accent"
                                                                    : "text-tables-secondaryIcon"
                                                            } ${
                                                                ability.can("edit", "product")
                                                                    ? "hover:text-tables-highlightedIcon cursor-pointer"
                                                                    : "cursor-default"
                                                            }`}
                                                            onClick={() =>
                                                                ability.can("edit", "product")
                                                                    ? history.push(
                                                                          `/dashboard/product-drafts/${draft.id}/edit`
                                                                      )
                                                                    : null
                                                            }
                                                            data-testid="product-drafts-action-edit"
                                                        />
                                                    </BasePermissionBlocker>

                                                    <BasePermissionBlocker
                                                        action="delete"
                                                        module="product"
                                                    >
                                                        <TrashSVG
                                                            className={`${
                                                                isCollapsed
                                                                    ? "text-accent"
                                                                    : "text-tables-secondaryIcon"
                                                            } ${
                                                                ability.can("delete", "product")
                                                                    ? "hover:text-tables-highlightedIcon cursor-pointer"
                                                                    : "cursor-default"
                                                            }`}
                                                            onClick={() =>
                                                                ability.can("delete", "product")
                                                                    ? setActiveProductDraftDeletion(
                                                                          draft
                                                                      )
                                                                    : null
                                                            }
                                                            data-testid="product-drafts-action-delete"
                                                        />
                                                    </BasePermissionBlocker>
                                                </div>
                                            );
                                        },
                                    },
                                ]}
                                alternate
                                isDataLoading={productDraftsLoading}
                                manualControls={{
                                    ordering: (newOrdering) => {
                                        if (!newOrdering) {
                                            setOrderBy(undefined);
                                            return;
                                        }

                                        let name: ProductDraft.DTO.OrderBy | undefined;

                                        switch (newOrdering.index) {
                                            case 1:
                                                name = "productName";
                                                break;
                                            case 5:
                                                name = "sellingPrice";
                                                break;
                                        }

                                        if (name) {
                                            setOrderBy({ name, type: newOrdering.order });
                                        }
                                    },
                                }}
                            />

                            <BaseTablePaginationControls
                                className="mt-[32px]"
                                pagination={pagination}
                                total={productDraftsData}
                            />
                        </div>
                    </BaseLoadingBlocker>
                </Can>
            </div>

            <ProductDraftsImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
            />

            <ProductDraftsExportModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                filters={filters}
                orderBy={orderBy}
            />

            <MaterializationModal
                isOpen={isMaterializationModalOpen}
                selectedIds={selectedDraftIds}
                onClose={() => setIsMaterializationModalOpen(false)}
                onAccepted={clearSelection}
            />

            <DeletionModal
                isOpen={!!activeProductDraftDeletion}
                entity={activeProductDraftDeletion}
                onClose={() => setActiveProductDraftDeletion(undefined)}
                onDeletion={() => {
                    if (activeProductDraftDeletion) {
                        setSelectedDraftIds((previous) =>
                            previous.filter((id) => id !== activeProductDraftDeletion.id)
                        );
                    }
                }}
            />
        </>
    );
}
